import React from "react";

import { Controller, useFormContext } from "react-hook-form";
import { Text } from "theme-ui";

import { Row } from "../../ui/box";
import { Card } from "../../ui/card";
import { Link } from "../../ui/link";
import { Message } from "../../ui/message";
import { Toggle } from "../../ui/toggle";
import { SyncStatusBadge } from "./sync-status-badge";

interface GitSyncableResourceToggleProps {
  fieldName: string;
  last_run_at?: string;
  error: any;
  description?: string;
  title: string;
  learnMoreUrl?: string;
  icon?: React.ReactNode;
  blocked?: boolean;
  onToggle?: (value: boolean) => void;
}

export const GitSyncableResourceToggle = ({
  fieldName,
  error,
  learnMoreUrl,
  title,
  icon,
  blocked,
  onToggle,
  description,
  last_run_at,
}: GitSyncableResourceToggleProps) => {
  const syncEnabled = useFormContext().getValues(fieldName);

  const header = (
    <Row sx={{ justifyContent: "space-between", alignItems: "center", pb: "12px" }}>
      <Row sx={{ alignItems: "center" }}>
        {icon}
        <Text sx={{ ml: icon ? 2 : 0, fontSize: "20px" }}>{title}</Text>
      </Row>
      <Controller
        name={fieldName}
        render={({ field }) => (
          <Toggle
            disabled={blocked}
            label={field.value ? "Enabled" : "Disabled"}
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              onToggle && onToggle(value);
            }}
          />
        )}
      />
    </Row>
  );
  const descriptionComponent = (
    <Row sx={{ flexDirection: "column", maxWidth: "80%" }}>
      <Row sx={{ pb: 1 }}>
        <Text sx={{ mb: 2, fontSize: 1, color: "base.5" }}>{description}</Text>
      </Row>
      {learnMoreUrl && (
        <Row>
          <Link newTab to={learnMoreUrl}>
            Learn more
          </Link>
        </Row>
      )}
    </Row>
  );

  const syncStatus = (
    <>
      <Row sx={{ justifyContent: "space-between", alignItems: "center", pt: "24px" }}>
        <Text sx={{ fontSize: "14px", fontWeight: 500, color: "#4B5565" }}>Sync Status</Text>
        <SyncStatusBadge error={error} lastAttemptedAt={last_run_at || ""} setup={!last_run_at} />
      </Row>
      {error && (
        <Row sx={{ pt: "24px" }}>
          <Message sx={{ width: "100%" }} variant="error">
            {error?.message && <Text as="b">{error.message}</Text>}
            <Text as="pre" sx={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }}>
              {JSON.stringify(error, null, 2)}
            </Text>
          </Message>
        </Row>
      )}
    </>
  );

  return (
    <Card size="small">
      {header}
      {description && descriptionComponent}
      {syncEnabled && !blocked && syncStatus}
    </Card>
  );
};
